import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { ImageInput } from '@adornis/file-utils/client/image-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { type FunctionalForm, useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { AddressForm } from '@martinus/address/client/AddressForm.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { openValidationErrorDialog } from '@martinus/validation/sg-validation-error-dialog.js';
import { html, nothing } from 'lit';
import { todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { isBillingLocked } from '../../api/groupBillingLock.js';
import { choose } from 'lit/directives/choose.js';
import { SgFormGridEntry } from '../../../client/components/SgFormGridEntry.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { config } from '../../../config.js';
import { Bundesland } from '../../../db/enums/misc-enums.js';
import { Permission } from '../../../permissions/roles.js';
import { GroupsAPI } from '../../api/GroupsAPI.js';
import { Group } from '../../entities/Group.js';
import { Kostenstelle } from '../../entities/Kostenstelle.js';
import { ActivatedRegisterType, School, type SchoolSubGroup, SchoolType } from '../../entities/School.js';
import { SGGroupCreationDialog } from '../../list-page/sg-group-creation-dialog.js';

const groupSelectionSet = {
  _id: 1,
  name: 1,
  billingLockStichtag: 1,
  billingLockReleaseDay: 1,
  kuerzel: 1,
  diputatsstunden: 1,
  bundesland: 1,
  kostenstellePrefix: 1,
  abwesenheitsemail: 1,
  microsoftDomain: 1,
  microsoftDomainForLogin: 1,
  infoMail: 1,
  telefonNr: 1,
  iban: 1,
  homepageURL: 1,
  susBewerbungReplyTo: 1,
  mode: 1,
  parentIDs: 1,
  kostenstelleSuffix: 1,
  accountingNumber: 1,
  imageID: 1,
  publicSchool: 1,
  isSgSchool: 1,
  linkGanztagsangebotInfo: 1,
  addBGMail: 1,
  bgMailPassword: 1,
  schoolType: 1,
  schulnummer: 1,
  image: {
    _id: 1,
    meta: {
      completeName: 1,
      fileName: 1,
      extension: 1,
      mimetype: 1,
      size: 1,
    },
  },
  adresse: {
    streetAndNumber: 1,
    zip: 1,
    city: 1,
    attentionLine: 1,
  },
  billingPosition: {
    _id: 1,
    name: 1,
    priceInCents: 1,
    rhythm: 1,
    kuerzel: 1,
    kostenstelleID: 1,
    buchungskonto: 1,
    templateID: 1,
  },
  microsoftGroupData: {
    mailNickname: 1,
    description: 1,
    secureGroup: 1,
    visibility: 1,
    microsoftID: 1,
    createdInEntra: 1,
  },
  parents: {
    _id: 1,
    name: 1,
    _class: 1,
  },
} as const satisfies BaseQLSelectionSet<Group | School | SchoolSubGroup | Kostenstelle>;

const SusRegistrationFormFields = (props: { form: FunctionalForm<School>; manuallyUpdateForm: () => void }) => {
  return html`
    ${SgFormGridEntry({
      label: 'Öffentlich?',
      render: html` <div class="flex flex-row gap-2 items-center">
        ${ToggleInput({
          ...props.form.field('publicSchool'),
        })}
        ${
          props.form.document?.publicSchool
            ? html` <span>Ja, die Gruppe wird in der Schüleranmeldung angezeigt.</span> `
            : html`
                <span class="text-base-content/70"
                  >Nein, die Gruppe wird <b>nicht</b> in der Schüleranmeldung angezeigt.</span
                >
              `
        }
      </div>`,
    })}
    ${SgFormGridEntry({
      label: 'Anmeldemodus',
      render: DropdownSelectionInput({
        // TODO typing for enums in selection input
        ...props.form.field('mode'),
        clearable: true,
        placeholder: 'Kein Anmeldemodus ausgewählt',
        placeholderMode: 'hide',
        selectables: Object.values(ActivatedRegisterType),
        renderString: (mode: ActivatedRegisterType) => {
          const trans = {
            [ActivatedRegisterType.ALL]: 'Alle Modi',
            [ActivatedRegisterType.ELEMENTARY_HIGH]: 'Nur Grundschule zur Sekundarschule',
            [ActivatedRegisterType.SCHOOL_SCHOOL]: 'Nur Schulwechsel',
          };
          return trans[mode];
        },
      }),
    })}
    ${SgFormGridEntry({
      label: 'Homepage URL',
      render: TextInput({
        ...props.form.field('homepageURL'),
        clearable: true,
        placeholder: 'Keine Homepage URL hinterlegt',
        placeholderMode: 'hide',
        suffix: html`
          <button
            type="button"
            class="cursor-pointer flex items-center"
            tabindex="-1"
            @click=${() =>
              props.form.document?.homepageURL ? window.open(props.form.document.homepageURL, '_blank') : undefined}
          >
            <span class="icon-[material-symbols--link] text-base line-height-1"></span>
          </button>
        `,
      }),
    })}
    ${SgFormGridEntry({
      label: 'Link zur Ganztagsangebot Info',
      render: TextInput({
        ...props.form.field('linkGanztagsangebotInfo'),
        clearable: true,
        placeholder: 'Link zur Ganztagsangebot Info',
        placeholderMode: 'hide',
        suffix: html`
          <button
            type="button"
            class="cursor-pointer flex items-center"
            tabindex="-1"
            @click=${() =>
              props.form.document?.linkGanztagsangebotInfo
                ? window.open(props.form.document.linkGanztagsangebotInfo, '_blank')
                : undefined}
          >
            <span class="icon-[material-symbols--link] text-base line-height-1"></span>
          </button>
        `,
      }),
    })}
    ${SgFormGridEntry({
      label: 'Bewerberpostfach',
      render: TextInput({
        ...props.form.field('susBewerbungReplyTo'),
        placeholder: 'Keine Bewerberpostfach hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
    ${SgFormGridEntry({
      label: 'Schul-Info Mail',
      render: TextInput({
        ...props.form.field('infoMail'),
        placeholder: 'Keine Schul-Info Mail hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
  `;
};

/**
 * Explains the "Auch für Anmeldenamen" toggle. It renames the Microsoft login of a whole school, so the
 * admin has to know who is affected and what they have to tell those people before switching it.
 * @param schoolDomain the domain currently typed into the form, for the example
 */
const openLoginDomainInfoDialog = (schoolDomain: Maybe<string>) => {
  const tenantDomain = config.get('MS_TENANT_DOMAIN');
  const exampleDomain = schoolDomain?.trim().toLowerCase() || 'schul-domain.de';

  return showDialog<void>({
    content: (ok, cancel) => html`
      <div class="flex flex-col gap-4">
        ${DialogHeader({ title: 'Microsoft Domain für Anmeldenamen', close: () => cancel() })}
        <p>
          Ist der Schalter an, melden sich die Personen dieser Schule bei Microsoft mit der Domain der Schule an statt
          mit <b>@${tenantDomain}</b>. Die E-Mail-Adressen ändert der Schalter nicht.
        </p>
        <div class="flex flex-row flex-wrap items-center gap-2 rounded-box bg-base-200 p-3 font-mono">
          <span>2002797@${tenantDomain}</span>
          <span class="icon-[material-symbols--arrow-forward-rounded] size-5 text-base-content/70"></span>
          <span>2002797@${exampleDomain}</span>
        </div>

        <div class="flex flex-col gap-1">
          <span class="font-semibold">Wen betrifft es?</span>
          <ul class="list-disc pl-5">
            <li>
              Alle, deren Haupt-E-Mail-Adresse zu dieser Schule gehört: Mitarbeitende, Schülerinnen und Schüler, deren
              Sorgeberechtigte und Spender sowie ehemalige Mitarbeitende.
            </li>
            <li>
              Wer an mehreren Schulen ist, folgt einer Schule: zuerst der Schule, an der die Person arbeitet, dann der
              Schule, an der sie lernt, dann der Schule ihrer Kinder, zuletzt der Schule, an der sie gearbeitet hat.
            </li>
            <li>Von Hand gesetzte Anmeldenamen bleiben, wie sie sind.</li>
          </ul>
        </div>

        <div class="flex flex-col gap-1">
          <span class="font-semibold">Was passiert?</span>
          <ul class="list-disc pl-5">
            <li>
              Nach dem Speichern werden alle betroffenen Personen in Entra umbenannt. Das läuft im Hintergrund und kann
              bei einer großen Schule einige Stunden dauern.
            </li>
            <li>
              Dasselbe passiert, wenn die Domain bei eingeschaltetem Schalter geändert wird. Beim Ausschalten gehen die
              Anmeldenamen zurück auf @${tenantDomain}.
            </li>
            <li>
              Das Passwort bleibt gleich. Die Personen werden <b>nicht</b> automatisch benachrichtigt und brauchen den
              neuen Anmeldenamen für ihre nächste Anmeldung.
            </li>
          </ul>
        </div>

        <div role="alert" class="alert alert-warning alert-soft items-start text-base-content">
          <span class="icon-[material-symbols--warning-outline-rounded] size-5 shrink-0"></span>
          <span>
            Die Domain muss in Entra als verifizierte Domain eingetragen sein. Sonst lehnt Entra die Umbenennung ab
            (sichtbar im Microsoft-Tab der Person) und für neue Personen dieser Schule kann kein Konto angelegt werden.
          </span>
        </div>

        <div class="flex justify-end">
          <button type="button" class="btn btn-primary" @click=${() => ok()}>Verstanden</button>
        </div>
      </div>
    `,
  }).catch(() => undefined);
};

const SchoolFields = (props: { form: FunctionalForm<School>; manuallyUpdateForm: () => void; locked: boolean }) => {
  return html`
    ${SgFormGridEntry({
      label: 'Ist SG-Schule',
      render: ToggleInput({
        ...props.form.field('isSgSchool'),
        label: 'Ist die Schule eine Schule der Schulgesellschaft?',
      }),
    })}
    ${SgFormGridEntry({
      label: 'Telefonnummer',
      render: TextInput({
        ...props.form.field('telefonNr'),
        placeholder: 'Keine Telefonnummer hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
    ${SgFormGridEntry({
      label: 'Bundesland',
      render: DropdownSelectionInput({
        ...props.form.field('bundesland'),
        clearable: true,
        placeholder: 'In welchem Bundesland befindet sich die Schule?',
        placeholderMode: 'hide',
        search: (s: string) => {
          if (!s) return Object.values(Bundesland);
          return Object.values(Bundesland).filter(b => b.toLowerCase().includes(s.toLowerCase()));
        },
      }),
    })}
    ${SgFormGridEntry({
      label: 'Schulnummer',
      render: TextInput({
        ...props.form.field('schulnummer'),
        placeholder: 'Keine Schulnummer hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
    ${SgFormGridEntry({
      label: 'Schultyp',
      render: DropdownSelectionInput({
        ...props.form.field('schoolType'),
        clearable: true,
        placeholder: 'Keine Schultyp hinterlegt',
        placeholderMode: 'hide',
        selectables: Object.values(SchoolType),
      }),
    })}
    ${SgFormGridEntry({
      label: 'Deputatsstunden',
      render: NumberInput({ ...props.form.field('diputatsstunden'), clearable: true }),
    })}
    ${SgFormGridEntry({
      label: 'Abwesenheitsemail',
      render: TextInput({
        ...props.form.field('abwesenheitsemail'),
        placeholder: 'Keine Abwesenheitsemail hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
    ${SgFormGridEntry({
      label: 'Kostenstelle Prefix',
      render: TextInput({
        ...props.form.field('kostenstellePrefix'),
        disabled: props.locked,
        placeholder: 'Kein Kostenstellen Prefix hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
    ${SgFormGridEntry({
      label: 'Buchhaltungsmandant',
      render: TextInput({
        ...props.form.field('accountingNumber'),
        disabled: props.locked,
        placeholder: 'Kein Buchhaltungsmandant hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
    ${SgFormGridEntry({
      label: 'Microsoft Domain',
      render: html`
        <div class="flex flex-row flex-wrap items-center gap-4">
          <div class="grow">
            ${TextInput({
              ...props.form.field('microsoftDomain'),
              placeholder: 'Keine Microsoft Domain hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
            })}
          </div>
          <div class="flex flex-row items-center gap-1">
            ${ToggleInput({
              ...props.form.field('microsoftDomainForLogin'),
              label: 'Auch für Anmeldenamen',
            })}
            <button
              type="button"
              class="btn btn-ghost btn-sm btn-square"
              aria-label="Was bewirkt das?"
              @click=${() => void openLoginDomainInfoDialog(props.form.document?.microsoftDomain)}
            >
              <span class="icon-[material-symbols--info-outline] size-5 text-base-content/70"></span>
            </button>
          </div>
        </div>
      `,
    })}
    ${SgFormGridEntry({
      label: 'ADD-BG-Mail',
      render: TextInput({
        ...props.form.field('addBGMail'),
        placeholder: 'Keine ADD-BG-Mail hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
    ${SgFormGridEntry({
      label: 'ADD-BG-Mail Passwort',
      render: TextInput({
        ...props.form.field('bgMailPassword'),
        type: 'password',
        placeholder: 'Kein Passwort hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
  `;
};

const KostenstelleFields = (props: { form: FunctionalForm<Kostenstelle>; locked: boolean }) => {
  return html`
    ${SgFormGridEntry({
      label: 'Kostenstelle Suffix',
      render: TextInput({
        ...props.form.field('kostenstelleSuffix'),
        disabled: props.locked,
        placeholder: 'Keine Kostenstelle Suffix hinterlegt',
        placeholderMode: 'hide',
        clearable: true,
      }),
    })}
  `;
};

const SubGroupsCard = stateful((props: { groupID: string }) => {
  const [view, setView] = useState(
    new View({
      filter: new AdornisFilter({
        searchFields: ['name'],
        aspects: [new StringAspect({ customID: 'groupID', value: props.groupID })],
      }),
    }),
  );

  const user = useObservable(() => currentUser(), []);

  const options = buildFullOptions({
    class: Group,
    operation: GroupsAPI.subscribeSubGroups,
    view,
    rowHref(entity) {
      return `/groups/${entity._id}`;
    },
    customColumns: [
      {
        fieldName: 'name',
        columnName: 'Name',
      },
      {
        fieldName: '_class',
        columnName: 'Typ',
      },
      {
        fieldName: 'kostenstelleSuffix',
        columnName: 'Suffix',
      },
    ],
  });

  return html`
    <div class="card card-body flex-col">
      ${CardHeader({
        title: 'Untergeordnete Gruppen',
        actionsDivider: true,
        actions: user?.hasPermission(Permission.WRITE_KOSTENSTELLE, props.groupID)
          ? html`<button
              class="btn btn-success"
              @click=${() => {
                void SGGroupCreationDialog(true, {
                  pickedClass: Kostenstelle._class,
                  pickedSchoolIDs: [props.groupID],
                });
              }}
            >
              <span class="icon-[material-symbols--add-rounded]"></span>
            </button>`
          : nothing,
      })}
      ${TextInput({
        value: view.filter?.search,
        placeholder: 'Suche',
        clearable: true,
        prefix: html`<span class="icon-[material-symbols--search]"></span>`,
        onChanged: search => {
          if (!view.filter) return;
          view.filter.search = search ?? '';
          setView(view);
        },
      })}
      <div class="flex flex-col grow overflow-auto" style="height:400px">
        <x-data-view .options=${options.dataViewOptions}></x-data-view>
      </div>
    </div>
  `;
});

const loadingSkeleton = html`
  <div class="card-container">
    <div class="card card-body flex-col">
      ${CardHeader({ title: 'Informationen' })}
      <div class="grid grid-cols-[1fr_3fr] items-center gap-4">
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
      </div>
    </div>

    <div class="card card-body flex-col">
      ${CardHeader({ title: 'Untergeordnete Gruppen' })}
      <div class="grid grid-cols-[1fr_3fr] items-center gap-4">
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
        <div class="skeleton h-10"></div>
      </div>
    </div>
  </div>
`;

export const SgGroupDetailsEdit = stateful(({ groupId }: { groupId: Maybe<string> }) => {
  const { value: group, loading } = useQuery(() => subscribeByID(Group, groupId)(groupSelectionSet), [groupId]);
  const user = useObservable(() => currentUser(), []);

  if (loading || !group) return loadingSkeleton;

  const form = useForm(group, {
    validator: null,
  });
  // what the export reads is read only while the group is in billing; the server refuses it too (`assertNotInBilling`)
  const billingLocked = isBillingLocked(group, todayAsCalendarDay());

  const handleSave = async () => {
    const formErrors = await form.validate();
    if (formErrors) {
      await openValidationErrorDialog({ error: formErrors });
      return;
    }

    await guardDialog(
      async () => {
        if (group._id) {
          // uploads every picked file and writes the resulting file ids into the form document
          await form.uploadAllPendingFiles();
          await form.document.save();
        }
      },
      {
        success: () => {
          void SgSnackbar.show('Gruppe erfolgreich gespeichert');
        },
      },
    );
  };

  const handleDelete = async () => {
    if (
      !(await confirmDialog('Möchten Sie die Gruppe wirklich löschen?', {
        highlightDecline: true,
        declineText: 'Abbrechen',
        acceptText: 'Gruppe löschen',
      }))
    )
      return;

    if (!group) return;
    void guardDialog(
      async () => {
        if (group._id) {
          await group.remove();
        }
      },
      {
        success: () => {
          void SgSnackbar.show('Gruppe gelöscht');
          goTo('/groups');
        },
      },
    );
  };

  return html`
    <div class="card-container">
      <div class="card card-body flex-col">
        ${CardHeader({
          title: 'Informationen',
        })}
        <div class="flex gap-4 items-start">
          <div class="grid grid-cols-[1fr_4fr] items-center gap-4 grow">
            ${SgFormGridEntry({
              label: 'Name',
              render: TextInput({
                ...form.field('name'),
                clearable: true,
                placeholder: 'Kein Name hinterlegt',
                placeholderMode: 'hide',
              }),
            })}
            ${SgFormGridEntry({
              label: 'Kürzel',
              render: TextInput({
                ...form.field('kuerzel'),
                clearable: true,
                placeholder: 'Kein Kürzel hinterlegt',
                placeholderMode: 'hide',
              }),
            })}
            ${SgFormGridEntry({
              label: 'Übergeordnete Gruppe',
              render: EntityPickerInput({
                ...form.field('parentIDs'),
                disabled: billingLocked,
                multi: true,
                class: Group,
                filter: new AdornisFilter({
                  searchFields: ['name'],
                }),
                selectionSet: { _id: 1, _class: 1, name: 1 },
                // @ts-expect-error narrowed entity typing
                operation: GroupsAPI.subscribeGroups,
                onBadgeClicked: (item: Group) => {
                  goTo(`/groups/${item._id}`, {
                    target: '_blank',
                  });
                },
              }),
            })}
          </div>
          ${
            group._class === School._class
              ? html`
                  <div class="flex flex-col card bg-base-200 p-2">
                    <div class="w-[100px] h-[100px]">
                      ${ImageInput({
                        ...(form as unknown as FunctionalForm<School>).field('imageID'),
                        mode: 'width',
                      })}
                    </div>
                  </div>
                `
              : null
          }
        </div>

        <div class="grid grid-cols-[1fr_4fr] items-center gap-4">
          ${choose(group._class, [
            [School._class, () => SchoolFields({ form, locked: billingLocked })],
            [Kostenstelle._class, () => KostenstelleFields({ form, locked: billingLocked })],
          ])}
        </div>
      </div>

      ${choose(group._class, [
        [
          School._class,
          () => html`
            <div class="card card-body flex-col">
              ${CardHeader({
                title: 'Adresse',
              })}
              ${AddressForm({
                form,
                streetAndNumberFieldPath: ['adresse', 'streetAndNumber'],
                zipFieldPath: ['adresse', 'zip'],
                cityFieldPath: ['adresse', 'city'],
                attentionLineFieldPath: ['adresse', 'attentionLine'],
              })}
            </div>

            <div class="card card-body flex-col">
              ${CardHeader({
                title: 'Schüleranmeldung',
              })}
              <div class="grid grid-cols-[1fr_4fr] items-center gap-4">${SusRegistrationFormFields({ form })}</div>
            </div>
          `,
        ],
      ])}
      ${SubGroupsCard({ groupID: group._id })}

      <div class="h-30"></div>
    </div>
    <div
      class="fixed bottom-0 z-20 flex justify-end gap-4 p-4 border-t border-base-300 bg-base-100/80 shadow-[0_-8px_24px_-12px_rgb(16_42_49/0.18)] backdrop-blur-md backdrop-saturate-150"
      ${css({
        width: navigator.userAgent.toLowerCase().includes('firefox') ? '-moz-available' : '-webkit-fill-available',
      })}
    >
      ${
        user?.hasPermission(Permission.DELETE_GROUP)
          ? html`<button class="btn btn-ghost btn-error btn-lg" @click=${handleDelete}>
              <span class="icon-[material-symbols--delete-outline-rounded] size-5"></span>
              Löschen
            </button>`
          : nothing
      }
      <button class="btn btn-primary btn-lg" @click=${handleSave}>
        <span class="icon-[material-symbols--save-outline-rounded] size-5"></span>
        Speichern
      </button>
    </div>
  `;
});
